import { useState } from 'react';
import { CheckCircle2, ClipboardCopy, Loader2, Lock, PenLine, Unlock } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import { CryptoPrivacyBanner } from '../components/CryptoPrivacyBanner';
import {
  rsaDecrypt,
  rsaEncrypt,
  rsaSign,
  rsaVerify,
  type RsaHash,
} from '../lib/rsa';

type Mode = 'encrypt' | 'decrypt' | 'sign' | 'verify';
type SigScheme = 'pss' | 'pkcs1';

const HASHES: RsaHash[] = ['SHA-256', 'SHA-384', 'SHA-512'];

const MODE_LABEL: Record<Mode, string> = {
  encrypt: '加密 (OAEP)',
  decrypt: '解密 (OAEP)',
  sign: '签名',
  verify: '验签',
};

/**
 * `/tools/crypto/rsa` — RSA encrypt/decrypt + sign/verify.
 *
 * Pairs with `/tools/crypto/rsa-keygen`:
 *   - encrypt/decrypt 走 RSA-OAEP（key 由 `rsa-keygen` 的 OAEP scheme 生成）
 *   - sign/verify 走 PSS 或 PKCS1-v1_5（对应 keygen 的 PSS / PKCS1 scheme）
 *
 * Plaintext / message 按 UTF-8 编码；密文 / 签名都是 base64。
 */
export default function RsaTool(): JSX.Element {
  const [mode, setMode] = useState<Mode>('encrypt');
  const [sigScheme, setSigScheme] = useState<SigScheme>('pss');
  const [hash, setHash] = useState<RsaHash>('SHA-256');
  const [keyPem, setKeyPem] = useState('');
  const [input, setInput] = useState('');
  const [signature, setSignature] = useState('');
  const [output, setOutput] = useState('');
  const [verifyResult, setVerifyResult] = useState<null | boolean>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const onRun = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setOutput('');
    setVerifyResult(null);
    try {
      switch (mode) {
        case 'encrypt': {
          const r = await rsaEncrypt(keyPem, input, hash);
          setOutput(r);
          break;
        }
        case 'decrypt': {
          const r = await rsaDecrypt(keyPem, input, hash);
          setOutput(r);
          break;
        }
        case 'sign': {
          const r = await rsaSign(keyPem, input, sigScheme, hash);
          setOutput(r);
          break;
        }
        case 'verify': {
          const ok = await rsaVerify(keyPem, input, signature, sigScheme, hash);
          setVerifyResult(ok);
          break;
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopy = async (): Promise<void> => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };

  const isSign = mode === 'sign' || mode === 'verify';
  const usesPublicKey = mode === 'encrypt' || mode === 'verify';

  return (
    <div className="flex flex-col gap-3 p-4">
      <ToolHeader
        toolId="rsa"
        category="crypto"
        description="RSA 加解密 + 签名验签。OAEP 走加解密；PSS / PKCS1-v1_5 走签名。配合 /tools/crypto/rsa-keygen 生成密钥对。所有运算走浏览器 SubtleCrypto。"
      />
      <CryptoPrivacyBanner />

      <section className="card-pad flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-[11px] uppercase tracking-wide text-ink-dim">操作</span>
        <div className="flex flex-wrap gap-1.5">
          {(['encrypt', 'decrypt', 'sign', 'verify'] as Mode[]).map((m) => (
            <Pill key={m} active={mode === m} onClick={() => setMode(m)}>
              {m === 'encrypt' && <Lock className="h-3.5 w-3.5" />}
              {m === 'decrypt' && <Unlock className="h-3.5 w-3.5" />}
              {m === 'sign' && <PenLine className="h-3.5 w-3.5" />}
              {m === 'verify' && <CheckCircle2 className="h-3.5 w-3.5" />}
              {MODE_LABEL[m]}
            </Pill>
          ))}
        </div>
        {isSign && (
          <>
            <span className="ml-3 text-[11px] uppercase tracking-wide text-ink-dim">
              签名方案
            </span>
            <div className="flex gap-1.5">
              <Pill active={sigScheme === 'pss'} onClick={() => setSigScheme('pss')}>
                PSS
              </Pill>
              <Pill active={sigScheme === 'pkcs1'} onClick={() => setSigScheme('pkcs1')}>
                PKCS1
              </Pill>
            </div>
          </>
        )}
        <span className="ml-3 text-[11px] uppercase tracking-wide text-ink-dim">摘要</span>
        <div className="flex gap-1.5">
          {HASHES.map((h) => (
            <Pill key={h} active={hash === h} onClick={() => setHash(h)}>
              {h}
            </Pill>
          ))}
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">
          {usesPublicKey ? '公钥（PEM）' : '私钥（PEM）'}
        </header>
        <textarea
          className="input min-h-[120px] resize-y font-mono text-[12px]"
          value={keyPem}
          onChange={(e) => setKeyPem(e.target.value)}
          spellCheck={false}
          placeholder={
            usesPublicKey
              ? '-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----'
              : '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'
          }
        />
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">
          {mode === 'encrypt' && '明文 (UTF-8)'}
          {mode === 'decrypt' && '密文 (base64)'}
          {mode === 'sign' && '消息 (UTF-8)'}
          {mode === 'verify' && '消息 (UTF-8)'}
        </header>
        <textarea
          className="input min-h-[100px] resize-y font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder={
            mode === 'decrypt'
              ? '密文 base64'
              : mode === 'sign' || mode === 'verify'
                ? '要签名 / 验签的消息'
                : '要加密的明文'
          }
        />
        {mode === 'verify' && (
          <>
            <header className="pt-1 text-[11px] uppercase tracking-wide text-ink-dim">
              签名 (base64)
            </header>
            <textarea
              className="input min-h-[80px] resize-y font-mono text-[12px]"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              spellCheck={false}
              placeholder="rsa sign 输出的 base64 签名"
            />
          </>
        )}
        <div>
          <button
            type="button"
            onClick={onRun}
            disabled={busy || !keyPem || !input || (mode === 'verify' && !signature)}
            className="btn"
          >
            {busy ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                处理中…
              </>
            ) : (
              MODE_LABEL[mode]
            )}
          </button>
        </div>
      </section>

      {error && (
        <div className="card-pad border-red-500/30 bg-red-500/5 text-[12px] text-red-500">
          {error}
        </div>
      )}

      {mode === 'verify' && verifyResult !== null && (
        <div
          className={clsx(
            'card-pad text-[13px]',
            verifyResult
              ? 'border-emerald-500/30 bg-emerald-500/5 text-emerald-500'
              : 'border-red-500/30 bg-red-500/5 text-red-500',
          )}
        >
          {verifyResult ? '✓ 签名有效' : '✗ 签名无效'}
        </div>
      )}

      {mode !== 'verify' && (
        <section className="card-pad flex flex-col gap-2">
          <header className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wide text-ink-dim">
              {mode === 'encrypt' && '密文 (base64)'}
              {mode === 'decrypt' && '明文'}
              {mode === 'sign' && '签名 (base64)'}
            </span>
            <button
              type="button"
              onClick={onCopy}
              disabled={!output}
              className="btn-ghost px-2 py-0.5"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                  已复制
                </>
              ) : (
                <>
                  <ClipboardCopy className="h-3 w-3" />
                  复制
                </>
              )}
            </button>
          </header>
          <textarea
            className="input min-h-[180px] resize-y font-mono text-[12px]"
            value={output}
            readOnly
            spellCheck={false}
            placeholder={busy ? '处理中…' : '点击按钮后在这里显示结果'}
          />
        </section>
      )}
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[12px] transition',
        active
          ? 'border-accent bg-accent/10 text-accent'
          : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
